import React from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Radio,
  Divider,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CompetitionSearchDialog from "./CompetitionSearchDialog";

function CompetitionLookupSection({
  classes,
  headerText,

  // inline search state
  dataForForm,
  setDataForForm,
  handleInputChange,
  runCompetitionSearch,
  searching,
  competitionIdError,

  // mini results dialog state
  searchDialogOpen,
  setSearchDialogOpen,
  searchResults,
  selectedCompIdx,
  setSelectedCompIdx,
  applySelection,
  setSearchResults,

  // advanced search dialog state
  compLookupOpen,
  setCompLookupOpen,
  CompSearchApi,
  setSelectedCompetition, // used only to clear
  dateFormatter,

  // styling helpers
  competitionButtonClass,
  competitionButtonStyle,
  sectionTitleClass,

  // misc
  setShowSearchPanel,
  selectedCompId,
  selectedCompetition,
}) {
  const isUSHJA = selectedCompId === 3;

  const competitionTitle = isUSHJA
    ? "Enter the USHJA Competition ID or search for the USHJA Competition ID below."
    : "Enter the USEF Competition ID or search for the USEF Competition ID below.";

  const hasSelectedCompetition = Boolean(
    selectedCompetition && selectedCompetition.competitionId
  );

  const buildCompetitionDates = (start, end) => {
    if (!start && !end) return "";
    const startStr = start ? dateFormatter(start) : "";
    const endStr = end ? dateFormatter(end) : "";
    if (startStr && endStr) return `${startStr} - ${endStr}`;
    return startStr || endStr;
  };

  const normalizeLocation = (row) =>
    row.competition_location ||
    row.location ||
    [row.city, row.state].filter(Boolean).join(", ") ||
    "";

  const resetInlineSearchState = () => {
    setShowSearchPanel(false);
    setSearchResults([]);
    setSelectedCompIdx(null);
    setDataForForm((prev) => ({ ...prev, competitionId: "" }));
  };

  const handleDialogClose = () => {
    setSearchDialogOpen(false);
    resetInlineSearchState();
  };

  const handleInlineSearch = () => {
    setSelectedCompetition(null);
    runCompetitionSearch();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (hasSelectedCompetition) {
        return;
      }
      handleInlineSearch();
    }
  };

  const handleOpenAdvancedSearch = () => {
    setSelectedCompetition(null);
    resetInlineSearchState();
    setCompLookupOpen(true);
  };

  const handleRowRadioChange = (idx) => {
    setSelectedCompIdx(idx);
  };

  const handleSelectFromMiniDialog = () => {
    if (selectedCompIdx == null) return;
    const chosen = searchResults[selectedCompIdx];
    const normalizedRow = {
      ...chosen,
      competition_location: normalizeLocation(chosen),
    };

    applySelection(normalizedRow);

    setSearchDialogOpen(false);
    resetInlineSearchState();
  };

  const handleAdvancedSelect = (row) => {
    const normalizedRow = {
      ...row,
      competition_location: normalizeLocation(row),
    };

    applySelection(normalizedRow);
    setCompLookupOpen(false);
  };

  return (
    <>
      {/* Title */}
      <Typography style={{ color: "#EC5269", fontWeight: 700, padding: "1%" }}>
        {headerText}
      </Typography>

      <Grid item xs={12}>
        <Typography className={classes.headerText}>
          {competitionTitle}
        </Typography>
      </Grid>

      {/* Inline Competition ID + search button */}
      <Grid container alignItems="flex-start" className={classes.searchFeature}>
        <Grid item>
          <TextField
            className={classes.lineLength}
            label="Competition ID"
            name="competitionId"
            value={dataForForm.competitionId ?? ""}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            required
            variant="standard"
            error={Boolean(competitionIdError)}
            helperText={competitionIdError}
            disabled={hasSelectedCompetition}
          />
        </Grid>
        <Grid item>
          <Button
            color="primary"
            className={classes.competitionButtonStyleContinue}
            variant="contained"
            aria-label="open competition lookup"
            onClick={handleInlineSearch}
            disabled={searching || hasSelectedCompetition}
          >
            Continue
          </Button>
        </Grid>
        <Grid
          item
          className={classes.competitionButton}
        >
          <Button
            color="primary"
            className={classes.competitionButtonStyle}
            variant="contained"
            onClick={handleOpenAdvancedSearch}
            disabled={hasSelectedCompetition}
          >
            Search Competitions
          </Button>
        </Grid>
      </Grid>

      {/* Advanced search dialog */}
      <CompetitionSearchDialog
        open={compLookupOpen}
        onClose={() => setCompLookupOpen(false)}
        Api={CompSearchApi}
        selectedCompId={selectedCompId}
        onSelect={handleAdvancedSelect}
      />

      {/* Mini results dialog (for inline search) */}
      <Dialog
        open={searchDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className={classes.headerText}>
          Select Competition:
          <IconButton
            onClick={handleDialogClose}
            aria-label="close"
            className={classes.close}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider
          style={{
            background: "#ec5269ff",
            opacity: 1,
            height: 5,
            marginBottom: 24,
            marginLeft: 12,
            marginRight: 12,
          }}
        />

        <DialogContent>
          <div className={classes.dialogBodySection}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerText}>Select</TableCell>
                  <TableCell className={classes.headerText}>Year</TableCell>
                  <TableCell className={classes.headerText}>Comp ID</TableCell>
                  <TableCell className={classes.headerText}>
                    Competition Name
                  </TableCell>
                  <TableCell className={classes.headerText}>
                    Competition Dates
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchResults.map((row, idx) => (
                  <TableRow
                    key={`${row.usef_competition_id}-${row.comp_year}-${idx}`}
                  >
                    <TableCell>
                      <Radio
                        checked={selectedCompIdx === idx}
                        onChange={() => handleRowRadioChange(idx)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{row.comp_year}</TableCell>
                    <TableCell>{row.usef_competition_id}</TableCell>
                    <TableCell>{row.competition_name}</TableCell>
                    <TableCell>
                      {buildCompetitionDates(row.start_date, row.end_date)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            className={classes.buttonStyle}
            onClick={handleSelectFromMiniDialog}
            disabled={selectedCompIdx == null}
          >
            Select
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.buttonStyle}
            onClick={handleDialogClose}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CompetitionLookupSection;
